/**
 * Transcript renderers for tool activity: git-style truncated diffs for
 * edits/writes and light syntax highlighting for bash commands.
 *
 * These produce PERMANENT transcript lines (printed above the run area), so
 * they may wrap in the scrollback without breaking any redraw math — but each
 * line is still clamped to the terminal width so a big hunk doesn't flood the
 * screen. Colors follow git/Claude/Codex conventions: dark green background
 * for added lines, dark red for removed.
 */

const RESET = "\x1b[0m";
const DIM = "\x1b[2m";
const ADD_BG = "\x1b[48;5;22m\x1b[38;5;254m"; // dark green bg, pale text
const DEL_BG = "\x1b[48;5;52m\x1b[38;5;254m"; // dark red bg, pale text

// Shared budget: a diff shows at most this many content rows per side before
// truncating to a "… +n more" line — enough to see the shape of a change
// without drowning the transcript.
const MAX_SIDE_LINES = 4;

function clamp(s: string, max: number): string {
  return s.length > max ? s.slice(0, Math.max(0, max - 1)) + "…" : s;
}

function cols(): number {
  return Math.max(20, process.stdout.columns || 80);
}

/** Expand tabs (a tab under a colored bg renders unpredictably wide). */
function flat(line: string): string {
  return line.replace(/\t/g, "  ");
}

/**
 * A truncated git-style hunk for an exact-string edit: the lines removed
 * (red background, `-`) then the lines added (green background, `+`).
 * Common leading/trailing lines between old and new are trimmed away so the
 * hunk shows just the changed core. Returns [] when nothing visibly changed.
 */
export function diffLines(oldStr: string, newStr: string): string[] {
  let oldLines = oldStr.split("\n");
  let newLines = newStr.split("\n");
  // Trim shared prefix/suffix lines so only the real change shows.
  while (oldLines.length && newLines.length && oldLines[0] === newLines[0]) {
    oldLines.shift();
    newLines.shift();
  }
  while (
    oldLines.length &&
    newLines.length &&
    oldLines[oldLines.length - 1] === newLines[newLines.length - 1]
  ) {
    oldLines.pop();
    newLines.pop();
  }
  const width = cols() - 2;
  const out: string[] = [];
  const side = (lines: string[], bg: string, sign: string) => {
    const shown = lines.slice(0, MAX_SIDE_LINES);
    for (const l of shown) out.push(`${bg}${sign} ${clamp(flat(l), width)}${RESET}`);
    const hidden = lines.length - shown.length;
    if (hidden > 0) out.push(`${DIM}  … +${hidden} more line${hidden === 1 ? "" : "s"}${RESET}`);
  };
  side(oldLines, DEL_BG, "-");
  side(newLines, ADD_BG, "+");
  return out;
}

/** A truncated all-added hunk for a newly written file. */
export function newFileLines(content: string): string[] {
  const lines = content.split("\n");
  const width = cols() - 2;
  const shown = lines.slice(0, MAX_SIDE_LINES);
  const out = shown.map((l) => `${ADD_BG}+ ${clamp(flat(l), width)}${RESET}`);
  const hidden = lines.length - shown.length;
  if (hidden > 0) out.push(`${DIM}  … +${hidden} more line${hidden === 1 ? "" : "s"}${RESET}`);
  return out;
}

/**
 * Muted shell rendering for a command shown in the transcript: program words
 * (start / after a control operator) at normal intensity, EVERYTHING else —
 * arguments, strings, flags, operators, comments — dim. The transcript should
 * read as a quiet activity log, not a syntax rainbow; the program names are
 * enough to scan what ran. Quote-aware single-pass tokenizer; falls back to
 * dim text on anything it doesn't understand.
 */
export function highlightBash(cmd: string): string {
  let out = "";
  let i = 0;
  let expectProgram = true; // next bare word is a program name (start / after an operator)
  while (i < cmd.length) {
    const ch = cmd[i];
    if (ch === "'" || ch === '"') {
      // Whole quoted string (including quotes), dim; honor backslash escapes in "".
      let j = i + 1;
      while (j < cmd.length && cmd[j] !== ch) {
        if (ch === '"' && cmd[j] === "\\") j++;
        j++;
      }
      out += `${DIM}${cmd.slice(i, Math.min(j + 1, cmd.length))}${RESET}`;
      i = j + 1;
      continue;
    }
    if (ch === "#") {
      out += `${DIM}${cmd.slice(i)}${RESET}`;
      break;
    }
    const op = cmd.slice(i).match(/^(\|\||&&|\||;|>>|>|<)/);
    if (op) {
      out += `${DIM}${op[1]}${RESET}`;
      i += op[1].length;
      expectProgram = true;
      continue;
    }
    const word = cmd.slice(i).match(/^[^\s'"#|;&<>]+/);
    if (word) {
      const w = word[0];
      if (expectProgram && !w.startsWith("-")) {
        out += w; // program name at normal intensity — the only non-dim ink
        expectProgram = false;
      } else {
        out += `${DIM}${w}${RESET}`;
      }
      i += w.length;
      continue;
    }
    out += `${DIM}${ch}${RESET}`;
    i++;
  }
  return out;
}

/**
 * Collapse a (possibly multi-line, heredoc-y) command into one calm
 * transcript line: newlines become ⏎, runs of whitespace collapse, and the
 * result is clamped so it can't flood the transcript. Multi-line command
 * bodies belong in the child process, not the activity log.
 */
export function oneLineCommand(cmd: string, max: number): string {
  const collapsed = cmd.replace(/\s*\n+\s*/g, " ⏎ ").replace(/[ \t]{2,}/g, " ").trim();
  return clamp(collapsed, max);
}

// ── brand gradient ───────────────────────────────────────────────────────────

/**
 * The Standard Code brand gradient (amber → coral → violet), applied per
 * character with 24-bit ANSI color. `phase` shifts the gradient's starting
 * point (0..1) so multi-line art can sweep diagonally. Terminals without
 * truecolor get a 256-color approximation — never plain text, the brand
 * moments should always land.
 */
const GRAD_STOPS: Array<[number, number, number]> = [
  [0xff, 0xb3, 0x5c], // amber
  [0xff, 0x5c, 0x87], // coral
  [0x8b, 0x6c, 0xff], // violet
];
const GRAD_256 = [215, 210, 204, 176, 141]; // amber→pink→violet approximation

function truecolor(): boolean {
  const ct = process.env.COLORTERM ?? "";
  if (/truecolor|24bit/i.test(ct)) return true;
  // Common truecolor terminals that don't always set COLORTERM.
  return /iterm|kitty|wezterm|ghostty|alacritty/i.test(process.env.TERM_PROGRAM ?? "");
}

function gradAt(t: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, t));
  const seg = clamped * (GRAD_STOPS.length - 1);
  const i = Math.min(GRAD_STOPS.length - 2, Math.floor(seg));
  const k = seg - i;
  const [a, b] = [GRAD_STOPS[i], GRAD_STOPS[i + 1]];
  return [
    Math.round(a[0] + (b[0] - a[0]) * k),
    Math.round(a[1] + (b[1] - a[1]) * k),
    Math.round(a[2] + (b[2] - a[2]) * k),
  ];
}

/** Ping-pong a time/position value through [0,1] so a looping animation sweeps
 *  amber→coral→violet→coral→amber with no seam at the wrap point. */
function cyclePos(t: number): number {
  const p = t - Math.floor(t);
  return p < 0.5 ? p * 2 : 2 - p * 2;
}

function colorAtCycle(tri: number, tc: boolean): string {
  if (tc) {
    const [r, g, b] = gradAt(tri);
    return `\x1b[38;2;${r};${g};${b}m`;
  }
  return `\x1b[38;5;${GRAD_256[Math.min(GRAD_256.length - 1, Math.floor(tri * GRAD_256.length))]}m`;
}

/** ANSI fg escape that cycles through the brand gradient over time — for
 *  animated glyphs like the working spinner. `ms` is a clock (Date.now()). */
export function brandCycleColor(ms: number, periodMs = 1200): string {
  return colorAtCycle(cyclePos(ms / periodMs), truecolor());
}

/**
 * Seamless looping brand gradient sample (amber → coral → violet → amber).
 * `t` is any real; only the fractional part matters. Used for rotating border
 * highlights so the sweep has no hard seam.
 */
export function brandLoopFg(t: number): string {
  const p = t - Math.floor(t);
  // 4 stops with wrap-back to amber so the loop is continuous.
  const stops: Array<[number, number, number]> = [
    GRAD_STOPS[0],
    GRAD_STOPS[1],
    GRAD_STOPS[2],
    GRAD_STOPS[0],
  ];
  const seg = p * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(seg));
  const k = seg - i;
  const a = stops[i];
  const b = stops[i + 1];
  const r = Math.round(a[0] + (b[0] - a[0]) * k);
  const g = Math.round(a[1] + (b[1] - a[1]) * k);
  const bl = Math.round(a[2] + (b[2] - a[2]) * k);
  if (truecolor()) return `\x1b[38;2;${r};${g};${bl}m`;
  // 256-color: pick the nearest stop by segment.
  const approx = [215, 210, 204, 141, 215];
  return `\x1b[38;5;${approx[Math.min(approx.length - 1, Math.round(p * (approx.length - 1)))]}m`;
}

/** Apply the brand gradient across a single line of text. */
export function gradientText(text: string, phase = 0): string {
  const chars = [...text];
  const visible = chars.filter((ch) => ch.trim().length > 0).length;
  if (!visible) return text;
  const tc = truecolor();
  let seen = 0;
  let out = "";
  for (const ch of chars) {
    if (ch.trim().length === 0) { out += ch; continue; }
    const t = Math.min(1, phase + (seen / Math.max(1, visible - 1)) * (1 - phase * 0.4));
    seen++;
    if (tc) {
      const [r, g, b] = gradAt(t);
      out += `\x1b[38;2;${r};${g};${b}m${ch}`;
    } else {
      out += `\x1b[38;5;${GRAD_256[Math.min(GRAD_256.length - 1, Math.floor(t * GRAD_256.length))]}m${ch}`;
    }
  }
  return out + "\x1b[0m";
}

/**
 * Muted brand-tinted chrome for HUD box borders — warm amber-ink that sits
 * with the amber→coral→violet palette without competing with content.
 * Matches the dim line colors on standardcode.ai.
 */
export function brandChromeColor(): string {
  if (truecolor()) return "\x1b[38;2;120;100;88m"; // muted amber
  return "\x1b[38;5;180m";
}

/** Solid brand stop (amber / coral / violet) for small chrome labels. */
export function brandStopColor(stop: "amber" | "coral" | "violet"): string {
  if (truecolor()) {
    const [r, g, b] =
      stop === "amber" ? GRAD_STOPS[0] : stop === "coral" ? GRAD_STOPS[1] : GRAD_STOPS[2];
    return `\x1b[38;2;${r};${g};${b}m`;
  }
  return stop === "amber"
    ? "\x1b[38;5;215m"
    : stop === "coral"
      ? "\x1b[38;5;204m"
      : "\x1b[38;5;141m";
}

